<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination, Autoplay, SwiperOptions } from 'swiper';
import { ref } from 'vue';
import IconArrowSmall from '~/components/icons/IconArrowSmall.vue';

const route = useRoute();
const modules = [Navigation, Pagination, Autoplay];
const wrapperClass = ref('');
const hideBlock = ref(true);
const swiperRef = ref<typeof Swiper | null>(null);
let swiperInstance: InstanceType<typeof Swiper> | null = null;

interface Item {
  title: string | null;
  description: string | null;
  link: string | null;
  guid: string | null;
  pubDate: string | null;
  category: string | null;
  info: string | null;
  enclosure: string | null;
}

const props = defineProps<{
  id: number;
  posts_number: number;
  title: string;
  rss_url: string;
}>();

const swiperOptions = ref<SwiperOptions>({
  modules,
  direction: 'horizontal',
  loop: false,
  effect: 'fade',
  spaceBetween: 16,
  autoplay: false,
  slidesPerView: 'auto',
  pagination: {
    el: '.swiper-pagination',
    clickable: true,
  },
  navigation: {
    nextEl: '.swiper-btn-next',
    prevEl: '.swiper-btn-prev',
  },
  breakpoints: {
    0: {
      centeredSlides: true,
    },
    480: {
      centeredSlides: false,
      spaceBetween: 16,
    },
  },
});

const rssItems = ref<Item[]>([]);
const loadedImages = ref();

async function getRssItems () {
  const { data, error } = await useFetch(`${window.location.origin}${route.path.replace(/\/$/, '')}/api/fetchJson`, {
    params: { url: props.rss_url },
  }).finally(() => {
    hideBlock.value = false;
  });

  if (data.value) {
    const text = data.value;
    const parser = new DOMParser();
    const xml = parser.parseFromString(text, 'application/xml');
    const items = xml.querySelectorAll('item');
    const namespace = 'https://qic.online/en/blog/';

    rssItems.value = Array.from(items).map(item => ({
      title: item.querySelector('title')?.textContent,
      description: item.querySelector('description')?.textContent,
      link: item.querySelector('link')?.textContent,
      guid: item.querySelector('guid')?.textContent,
      pubDate: item.querySelector('pubDate')?.textContent,
      category: item.querySelectorAll('category')[0]?.textContent,
      info: items[0].getElementsByTagNameNS(namespace, 'timetoread')[0]?.textContent,
      enclosure: item.querySelector('enclosure')?.getAttribute('url'),
    }));
    loadedImages.value = new Array(rssItems.value.length).fill(false);
  } else {
    console.error('Error loading JSON: ', error);
    return false;
  }
}

function onImageLoad (index: number) {
  loadedImages.value[index] = true;
}

function checkState (state: string) {
  wrapperClass.value = state;
}

const tapBannerArrow = () => {
  // trackGtag('to_next_or_previous_banner', { page: 'main_page' });
};

onMounted(() => {
  if (swiperRef.value) {
    swiperInstance = swiperRef.value.swiper;
  }
});
onMounted(async () => {
  await nextTick();
  getRssItems();
});
onBeforeUnmount(() => {
  if (swiperInstance) {
    swiperInstance.destroy(true, true);
  }
});
</script>
<template>
  <section class="articles section-offset" :class="hideBlock ? 'hidden':''">
    <div class="container">
      <div class="articles__wrapper">
        <div class="articles__title">
          <h3>
            {{ props.title }}
          </h3>
          <div class="swiper-controls">
            <div class="swiper-btn-prev" @click="tapBannerArrow">
              <IconArrowSmall />
            </div>
            <div class="swiper-btn-next" @click="tapBannerArrow">
              <IconArrowSmall />
            </div>
          </div>
        </div>

        <div class="articles__list" :class="wrapperClass">
          <Swiper
            ref="swiperRef" v-bind="swiperOptions"
            @reach-beginning="checkState('beginning')"
            @reach-end="checkState('end')"
            @slide-change="checkState('end')"
          >
            <template v-for="(slide, index) in rssItems.slice(0, 10)">
              <SwiperSlide v-if="slide.enclosure" :key="index">
                <a :href="slide.link ?? '#'" target="_blank" class="articles__list-item">
                  <transition>
                    <div class="slide-picture">
                      <div v-if="!loadedImages[index]" class="loader" />
                      <img
                        :src="slide.enclosure"
                        :class="{ hidden: !loadedImages[index] }"
                        loading="lazy"
                        :alt="slide.title ?? ''"
                        @load="onImageLoad(index)"
                      >
                    </div>
                  </transition>
                  <div class="slide-wrapper">
                    <div class="slide-info">
                      {{ slide.info }}
                    </div>
                    <div class="slide-title">
                      {{ slide.title }}
                    </div>
                  </div>
                </a>
              </SwiperSlide>
            </template>

            <SwiperSlide v-if="rssItems.length > 0">
              <div class="slide-picture">
                <a
                  href="https://qic.online/en/blog/" target="_blank"
                  class="articles__list-item-last articles__list-button"
                >
                  <div class="title">{{ $t('main.articles.more.button') }}</div>
                  <div class="reads-logo"><img :alt="$t('main.articles.more.button')" src="@/assets/images/articles-button.svg"></div>
                  <div class="icon"><img :alt="$t('main.articles.more.button')" src="@/assets/images/arrow-narrow-right.svg"></div>
                </a>
              </div>
            </SwiperSlide>
          </Swiper>
          <div class="swiper-pagination" />
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@import '@/assets/scss/mixins.scss';
.articles {
  transition: all .2s linear;

  &.hidden {
    visibility: hidden;
    height: 0;
    padding: 0;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 32px 0 48px;
    gap: 32px;
    -ms-grid-gap: 32px;
    grid-gap: 32px;
    background: #F5F5F9;
    border-radius: 32px;
    @media(max-width: 767px) {
      padding: 24px 0 16px;
      gap: 16px;
    }
  }

  &__title {
    display: flex;
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
    padding: 0 32px;
    gap: 32px;
    -ms-grid-gap: 32px;
    grid-gap: 32px;

    @include md {
      justify-content: center;
    }

    h3 {
      color: #2D2D36;
      font-size: 36px;
      font-weight: 600;
      line-height: 42px;
      letter-spacing: -0.1px;
      @media (max-width: 767px) {
        font-size: 24px;
        line-height: 34px;
      }
      @media (max-width: 540px) {
        text-align: center;
      }
    }

    .swiper {
      &-controls {
        display: flex;
        flex-direction: row;
        gap: 16px;
        -ms-grid-gap: 16px;
        grid-gap: 16px;

        @media (max-width: 540px) {
          display: none;
        }
      }

      &-btn {
        &-next,
        &-prev {
          inset-inline-end: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #ffffff;
          border-radius: 100px;
          width: 32px;
          height: 32px;
          z-index: 1;
          cursor: pointer;
        }

        &-prev {
          transform: rotate(180deg);
        }
      }
    }

    @media(max-width: 1200px) {
      gap: 24px;
      -ms-grid-gap: 24px;
      grid-gap: 24px;
    }
    @media(max-width: 768px) {
      padding: 0 24px;
    }
  }

  &__list {
    position: relative;
    transition: all .2s linear;
    /*&.beginning {
      padding-left: 32px;
    }
    &.end {
      padding-right: 32px;
    }*/
    &-item {
      display: flex;
      flex-direction: column;
      gap: 16px;
      -ms-grid-gap: 16px;
      grid-gap: 16px;

      img {
        position: absolute;
        width: 100%;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        margin: auto;
      }

      @media(min-width: 580px) {
        max-width: 340px;
      }
    }

    .slide {
      &-wrapper {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 0 16px;
      }

      &-picture {
        position: relative;
        padding-bottom: 182px;
        border-radius: 16px;
        overflow: hidden;
        .loader{
          display: block;
          position: absolute;
          width: 100%;
          height: 100%;
          background-color: #f0f0f4;
          top: 0;
          left: 0;
          &:after{
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, #f0f0f4, lighten(#f0f0f4, 4%), #f0f0f4);
            animation: skeleton-shimmer 1.6s ease-in-out infinite;
          }
        }
        img{
          transition: all .1s linear;
          &.hidden{
            opacity: 0;
          }
        }
      }

      &-info {
        font-weight: 400;
        font-size: 12px;
        line-height: 14px;
        font-feature-settings: 'tnum' on, 'lnum' on;
        color: #9E9EA8;
      }

      &-title {
        font-weight: 400;
        font-size: 16px;
        line-height: 20px;
        letter-spacing: -0.1px;
        color: #2D2D36;
      }
    }

    &-button {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      position: absolute;
      color: #fff;
      width: 100%;
      height: 100%;
      background: #1A1B1F;
      border-radius: 380px;
      padding: 56px 58px;
      transition: all .2s ease-in;
      cursor: pointer;

      * {
        transition: all .4s ease-out;
      }

      .title {
        font-size: 12px;
        line-height: 14px;
        margin-bottom: 8px;
      }

      .icon {
        margin-top: 16px;
        width: 0;
        height: 0;
        opacity: 0;
      }

      &:hover {
        border-radius: 16px;

        .icon {
          width: 24px;
          height: 24px;
          opacity: 1;
        }
      }
    }
  }
}

.rtl {
  .articles {
    &__title {
      .swiper {
        &-btn {
          &-prev {
            transform: scaleX(1);
          }

          &-next {
            transform: scaleX(-1);
          }
        }
      }
    }

    &__list {
      &-button {
        .icon {
          transform: scaleX(-1);
        }
      }
    }
  }
}
</style>

<style lang="scss">
@import "swiper/swiper.css";

.swiper {
  overflow: hidden;
  padding: 0 32px;
  box-sizing: border-box;

  &-wrapper {
    display: flex;
    flex-wrap: nowrap;
    max-height: 300px;
  }

  &-slide {
    width: 264px;
  }

  &-pagination {
    display: none;
    flex-direction: row;
    justify-content: center;
    position: relative;
    gap: 8px;
    margin: 16px auto 0;
    top: 50%;
    z-index: 1;

    &-bullet {
      background: #CACAD8;
      width: 8px;
      height: 8px;
      display: block;
      border-radius: 8px;
      opacity: 1;
      cursor: pointer;
      transition: all .2s ease-in;

      &-active {
        width: 24px;
        background: #2D2D36;
      }
    }

    @media(max-width: 540px) {
      display: flex;
    }
  }

  &-button {
    &-disabled {
      svg {
        path {
          stroke: #CACAD8
        }
      }
    }
  }

  @media(max-width: 768px) {
    padding: 0 24px;
  }
}

@keyframes skeleton-shimmer {
  0% {
    transform: translate3d(-100%, 0, 0);
  }
  100% {
    transform: translate3d(100%, 0, 0);
  }
}
</style>
